/**
 * Cockpit Employés RH (LOT RH-02) — pilotage administratif dense (contrat, paie, conformité).
 *
 * RH = administratif uniquement : pas de zones, équipes, tournées ni disponibilité runtime
 * (voir `/admin/agents`).
 *
 * TODO : Person → TerrainProfile | RHProfile | UserAccount — identité non dupliquée.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  Loader2,
  PlusCircle,
  Users,
  ChevronRight,
  Link2,
  Search,
  RefreshCcw,
  FileSpreadsheet,
  FileDown,
  LayoutDashboard,
  Eye,
  Bell,
  CalendarClock,
  FileWarning,
  Landmark,
  UserX,
  ChevronUp,
  ChevronDown,
  Filter,
} from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../config/adminRoutes";
import {
  AdminPageHero,
  AdminEmptyState,
  AdminDataTableShell,
  AdminKpiCard,
  adminHeroActionClassName,
} from "../../modules/admin/ui";

type Kpis = {
  employesActifs: number;
  dossiersIncomplets: number;
  contratsExpirants30j: number;
  journaliers: number;
  masseSalarialeMensuelle: number;
};

type AlertesRh = {
  contratsExpirant30j: number;
  dossiersIncomplets: number;
  sansDocuments: number;
  sansCompteBancaire: number;
  sansUtilisateur: number;
};

export type EmployeRow = {
  id: number;
  nomComplet: string;
  matriculeRH: string;
  telephone: string | null;
  agentTerrainId: number | null;
  terrainLie: boolean;
  posteRH: string | null;
  typeContrat: string | null;
  statutRH: string;
  typeEmploye: string;
  agentTypeAgent: string | null;
  salaireMensuel: number | null;
  modePaiement: string | null;
  sansCompteBancaire: boolean;
  hasUserAccount: boolean;
  documentsStatut: string;
  documentsManquants: string[];
  dossierCompletPercent: number;
  compteUtilisateur: string;
  dateEmbauche: string | null;
  dateFinContrat: string | null;
  createdAt: string;
};

type AgentSans = {
  id: number;
  nomComplet: string;
  telephone?: string | null;
  typeAgent: string;
  fonctionTerrain: string | null;
  statutOperationnel: string | null;
  createdAt: string;
};

type DashResponse = {
  kpis: Kpis;
  alertes: AlertesRh;
  employes: EmployeRow[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  agentsSansDossierRH: AgentSans[];
};

type SortKey =
  | "nomComplet"
  | "matriculeRH"
  | "posteRH"
  | "typeContrat"
  | "statutRH"
  | "dateEmbauche"
  | "dateFinContrat"
  | "createdAt"
  | "dossierCompletPercent"
  | "salaireMensuel";

const ITEMS_PER_PAGE = 15;
const LIST_LIMIT = 200;

const STATUT_RH_OPTIONS = [
  "",
  "ACTIF",
  "EN_CONGE",
  "SUSPENDU",
  "FIN_CONTRAT",
  "ARCHIVE",
] as const;

const tdClass = "whitespace-nowrap px-2 py-1.5 align-middle text-[11px] text-slate-300";
const thClass =
  "whitespace-nowrap px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500";

function formatMoney(n: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "CDF",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatDateFr(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function createdRelative(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: frLocale });
  } catch {
    return "—";
  }
}

/** Libellé type agent (fiche terrain, sans zone/équipe). */
function libelleTypeAgentRaw(v?: string | null): string {
  const map: Record<string, string> = {
    TERRAIN: "Terrain",
    JOURNALIER: "Journalier",
    PERMANENT_TERRAIN: "Permanent terrain",
    CHAUFFEUR: "Chauffeur",
    CHEF_EQUIPE: "Chef d’équipe",
    SUPERVISEUR: "Superviseur",
    PRESTATAIRE: "Prestataire",
    EXTERNE: "Externe",
    SUPPORT_RH: "Support RH",
    ADMINISTRATIF: "Administratif",
    FINANCE: "Finance",
    RH: "RH",
    TECHNICIEN: "Technicien",
    AUTRE: "Autre",
  };
  const k = String(v ?? "").toUpperCase();
  return k ? map[k] ?? v ?? "—" : "—";
}

/** Statut opérationnel terrain (profil agent — pas affectation zone/équipe). */
function libelleStatutOperationnel(v?: string | null): string {
  const map: Record<string, string> = {
    DISPONIBLE: "Disponible",
    AFFECTE: "Affecté",
    EN_TOURNEE: "En tournée",
    INDISPONIBLE: "Indisponible",
    SUSPENDU: "Suspendu",
    ARCHIVE: "Archivé",
  };
  const k = String(v ?? "").toUpperCase().trim();
  return k ? map[k] ?? k : "—";
}

function contratExpirant30j(dateFin: string | null): boolean {
  if (!dateFin) return false;
  const end = new Date(dateFin);
  const now = new Date();
  const in30 = new Date(now);
  in30.setDate(in30.getDate() + 30);
  return end >= now && end <= in30;
}

function conformiteLabel(pct: number, docsIncomplete: boolean): "ok" | "attention" | "critique" {
  if (pct < 50 || docsIncomplete) return "critique";
  if (pct < 85) return "attention";
  return "ok";
}

const selectFilterClass =
  "min-w-[9rem] shrink-0 rounded-lg border border-slate-700 bg-slate-950/80 px-2 py-1.5 text-[11px] text-white outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30";

function RhRowBadges({ row }: { row: EmployeRow }): React.JSX.Element {
  const pct = row.dossierCompletPercent;
  const docsBad = row.documentsManquants.length > 0;
  const exp = contratExpirant30j(row.dateFinContrat);
  const stat = String(row.statutRH).toUpperCase();

  return (
    <div className="flex max-w-[200px] flex-wrap gap-0.5">
      {stat === "ACTIF" ? (
        <span className="rounded border border-emerald-500/40 bg-emerald-500/10 px-1 py-0 text-[9px] font-bold text-emerald-200">
          ACTIF
        </span>
      ) : null}
      {stat === "EN_CONGE" ? (
        <span className="rounded border border-violet-500/40 bg-violet-500/10 px-1 py-0 text-[9px] font-bold text-violet-200">
          EN_CONGÉ
        </span>
      ) : null}
      {stat === "SUSPENDU" ? (
        <span className="rounded border border-rose-500/40 bg-rose-500/10 px-1 py-0 text-[9px] font-bold text-rose-200">
          SUSPENDU
        </span>
      ) : null}
      {row.agentTypeAgent === "JOURNALIER" ? (
        <span className="rounded border border-amber-500/35 bg-amber-500/10 px-1 py-0 text-[9px] font-bold text-amber-100">
          JOURNALIER
        </span>
      ) : null}
      {(pct < 85 || docsBad) && (
        <span className="rounded border border-orange-500/40 bg-orange-500/10 px-1 py-0 text-[9px] font-bold text-orange-100">
          DOSSIER_INCOMPLET
        </span>
      )}
      {exp ? (
        <span className="rounded border border-amber-500/50 bg-amber-950/40 px-1 py-0 text-[9px] font-bold text-amber-100">
          CONTRAT_EXPIRANT
        </span>
      ) : null}
    </div>
  );
}

export default function ListeEmployesRHPage(): React.JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = hasPermission(user, "rhEmployes.manage");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [data, setData] = useState<DashResponse | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [filterTypeContrat, setFilterTypeContrat] = useState("");
  const [filterTypeEmploye, setFilterTypeEmploye] = useState("");
  const [filterCompletude, setFilterCompletude] = useState("");
  const [filterTerrain, setFilterTerrain] = useState("");
  const [filterConformite, setFilterConformite] = useState("");

  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const res = await api.get("/rh/employes/dashboard", {
        params: { page: 1, limit: LIST_LIMIT },
      });
      setData(res.data as DashResponse);
    } catch (e) {
      const ax = e as {
        response?: { data?: { detail?: string; message?: string; error?: string } };
      };
      const d =
        ax.response?.data?.message ??
        ax.response?.data?.detail ??
        ax.response?.data?.error;
      toast.error(
        typeof d === "string" && d.trim()
          ? `Chargement cockpit RH : ${d}`
          : "Chargement du cockpit RH impossible (droits ou réseau)."
      );
      setLoadError(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    filterStatut,
    filterTypeContrat,
    filterTypeEmploye,
    filterCompletude,
    filterTerrain,
    filterConformite,
    sortKey,
    sortDir,
  ]);

  const kpis = data?.kpis;
  const alertes = data?.alertes;
  const agentsSans = data?.agentsSansDossierRH ?? [];

  const distinctTypeContrats = useMemo(() => {
    const s = new Set<string>();
    for (const r of data?.employes ?? []) {
      const t = r.typeContrat?.trim();
      if (t) s.add(t);
    }
    return [...s].sort();
  }, [data?.employes]);

  const distinctTypeEmployes = useMemo(() => {
    const s = new Set<string>();
    for (const r of data?.employes ?? []) {
      if (r.typeEmploye && r.typeEmploye !== "—") s.add(r.typeEmploye);
    }
    return [...s].sort();
  }, [data?.employes]);

  const filteredEmployes = useMemo(() => {
    const rows = data?.employes ?? [];
    const q = debouncedSearch.toLowerCase();

    const list = rows.filter((row) => {
      const matchText =
        !q ||
        [
          row.nomComplet,
          row.matriculeRH,
          row.telephone,
          row.posteRH,
          row.typeContrat,
          row.statutRH,
          row.typeEmploye,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));

      const matchStatut =
        !filterStatut || String(row.statutRH).toUpperCase() === filterStatut.toUpperCase();

      const matchTc =
        !filterTypeContrat ||
        (row.typeContrat ?? "").toLowerCase() === filterTypeContrat.toLowerCase();

      const matchTe =
        !filterTypeEmploye || row.typeEmploye === filterTypeEmploye;

      const matchTerrain =
        filterTerrain === ""
          ? true
          : filterTerrain === "lie"
            ? row.terrainLie
            : !row.terrainLie;

      let matchComplet = true;
      if (filterCompletude === "ok") matchComplet = row.dossierCompletPercent >= 85;
      else if (filterCompletude === "incomplet")
        matchComplet = row.dossierCompletPercent < 85 && row.dossierCompletPercent >= 50;
      else if (filterCompletude === "critique") matchComplet = row.dossierCompletPercent < 50;

      const conf = conformiteLabel(
        row.dossierCompletPercent,
        row.documentsManquants.length > 0
      );
      const matchConf =
        !filterConformite || conf === filterConformite;

      return (
        matchText &&
        matchStatut &&
        matchTc &&
        matchTe &&
        matchTerrain &&
        matchComplet &&
        matchConf
      );
    });

    const dir = sortDir === "asc" ? 1 : -1;
    const sorted = [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "nomComplet":
          cmp = a.nomComplet.localeCompare(b.nomComplet, "fr");
          break;
        case "matriculeRH":
          cmp = a.matriculeRH.localeCompare(b.matriculeRH, "fr");
          break;
        case "posteRH":
          cmp = String(a.posteRH ?? "").localeCompare(String(b.posteRH ?? ""), "fr");
          break;
        case "typeContrat":
          cmp = String(a.typeContrat ?? "").localeCompare(String(b.typeContrat ?? ""), "fr");
          break;
        case "statutRH":
          cmp = String(a.statutRH).localeCompare(String(b.statutRH), "fr");
          break;
        case "dateEmbauche":
          cmp =
            (a.dateEmbauche ? new Date(a.dateEmbauche).getTime() : 0) -
            (b.dateEmbauche ? new Date(b.dateEmbauche).getTime() : 0);
          break;
        case "dateFinContrat":
          cmp =
            (a.dateFinContrat ? new Date(a.dateFinContrat).getTime() : 0) -
            (b.dateFinContrat ? new Date(b.dateFinContrat).getTime() : 0);
          break;
        case "createdAt":
          cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case "dossierCompletPercent":
          cmp = a.dossierCompletPercent - b.dossierCompletPercent;
          break;
        case "salaireMensuel":
          cmp =
            (a.salaireMensuel ?? -1) - (b.salaireMensuel ?? -1);
          break;
        default:
          cmp = 0;
      }
      return cmp * dir;
    });

    return sorted;
  }, [
    data?.employes,
    debouncedSearch,
    filterStatut,
    filterTypeContrat,
    filterTypeEmploye,
    filterCompletude,
    filterTerrain,
    filterConformite,
    sortKey,
    sortDir,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredEmployes.length / ITEMS_PER_PAGE));
  const pageClamped = Math.min(page, totalPages);

  const paginatedEmployes = useMemo(() => {
    const start = (pageClamped - 1) * ITEMS_PER_PAGE;
    return filteredEmployes.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredEmployes, pageClamped]);

  const emptyEmployes = !loading && !loadError && (data?.employes?.length ?? 0) === 0;
  const emptyFilters =
    !loading && !loadError && (data?.employes?.length ?? 0) > 0 && filteredEmployes.length === 0;

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "nomComplet" || key === "matriculeRH" ? "asc" : "desc");
    }
  };

  const SortBtn = ({
    label,
    k,
    className = "",
  }: {
    label: string;
    k: SortKey;
    className?: string;
  }) => (
    <button
      type="button"
      onClick={() => toggleSort(k)}
      className={`inline-flex items-center gap-0.5 font-bold hover:text-slate-300 ${className}`}
    >
      {label}
      {sortKey === k ? (
        sortDir === "asc" ? (
          <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )
      ) : (
        <span className="inline-block w-3 opacity-0">.</span>
      )}
    </button>
  );

  const resetFilters = () => {
    setSearchInput("");
    setFilterStatut("");
    setFilterTypeContrat("");
    setFilterTypeEmploye("");
    setFilterCompletude("");
    setFilterTerrain("");
    setFilterConformite("");
  };

  const handleRefresh = () => {
    void load();
    toast.success("Données actualisées");
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      filteredEmployes.map((row) => ({
        Matricule: row.matriculeRH,
        Employé: row.nomComplet,
        Téléphone: row.telephone ?? "",
        "Poste RH": row.posteRH ?? "",
        "Type contrat": row.typeContrat ?? "",
        "Statut RH": row.statutRH,
        "Début contrat": row.dateEmbauche ? formatDateFr(row.dateEmbauche) : "",
        "Fin contrat": row.dateFinContrat ? formatDateFr(row.dateFinContrat) : "",
        Création: formatDateFr(row.createdAt),
        Complétude: `${row.dossierCompletPercent}%`,
        Documents: row.documentsStatut,
        Paie: row.salaireMensuel != null ? row.salaireMensuel : "",
        "Mode paie": row.modePaiement ?? "",
        "Compte utilisateur": row.compteUtilisateur,
        "Terrain lié": row.terrainLie ? "Oui" : "Non",
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employés RH");
    XLSX.writeFile(wb, `employes_rh_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Export Excel généré");
  };

  const exportPDF = () => {
    if (!filteredEmployes.length) {
      toast.info("Aucun dossier à exporter");
      return;
    }
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Cockpit Employés RH", 14, 12);
    doc.setFontSize(9);
    doc.text(`Export du ${new Date().toLocaleString("fr-FR")}`, 14, 18);

    const body = filteredEmployes.map((row) => [
      row.nomComplet,
      row.matriculeRH,
      row.posteRH ?? "—",
      row.typeContrat ?? "—",
      row.statutRH,
      formatDateFr(row.dateEmbauche),
      formatDateFr(row.dateFinContrat),
      `${row.dossierCompletPercent}%`,
      row.documentsStatut,
      row.salaireMensuel != null ? formatMoney(row.salaireMensuel) : "—",
      row.compteUtilisateur,
      row.terrainLie ? "Oui" : "Non",
    ]);

    (doc as unknown as { autoTable: (o: object) => void }).autoTable({
      head: [
        [
          "Employé",
          "Matricule",
          "Poste",
          "Contrat",
          "Statut",
          "Début",
          "Fin",
          "%",
          "Docs",
          "Paie",
          "Compte",
          "Terrain",
        ],
      ],
      body,
      startY: 22,
      styles: { fontSize: 6 },
      headStyles: { fillColor: [30, 41, 59] },
    });

    doc.save(`cockpit_rh_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("Export PDF généré");
  };

  const heroActions = (
    <>
      {canManage ? (
        <Link to="/admin/rh/employes/nouveau" className={adminHeroActionClassName(false)}>
          Nouvel employé RH
          <ChevronRight className="h-4 w-4" />
        </Link>
      ) : null}
      <Link
        to="/admin/agents"
        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
      >
        <Users className="h-4 w-4" />
        Agents terrain
      </Link>
      <Link
        to="/admin/dashboard"
        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
      >
        <LayoutDashboard className="h-4 w-4" />
        Tableau de bord
      </Link>
    </>
  );

  const filterControls = (
    <>
      <select
        value={filterStatut}
        onChange={(e) => setFilterStatut(e.target.value)}
        className={selectFilterClass}
      >
        <option value="">Statut RH</option>
        {STATUT_RH_OPTIONS.filter(Boolean).map((s) => (
          <option key={s} value={s}>
            {String(s).replace(/_/g, " ")}
          </option>
        ))}
      </select>
      <select
        value={filterTypeContrat}
        onChange={(e) => setFilterTypeContrat(e.target.value)}
        className={selectFilterClass}
      >
        <option value="">Type contrat</option>
        {distinctTypeContrats.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <select
        value={filterTypeEmploye}
        onChange={(e) => setFilterTypeEmploye(e.target.value)}
        className={selectFilterClass}
      >
        <option value="">Type employé</option>
        {distinctTypeEmployes.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <select
        value={filterCompletude}
        onChange={(e) => setFilterCompletude(e.target.value)}
        className={selectFilterClass}
      >
        <option value="">Complétude</option>
        <option value="ok">≥ 85 %</option>
        <option value="incomplet">50 – 84 %</option>
        <option value="critique">&lt; 50 %</option>
      </select>
      <select
        value={filterTerrain}
        onChange={(e) => setFilterTerrain(e.target.value)}
        className={selectFilterClass}
      >
        <option value="">Terrain</option>
        <option value="lie">Lié</option>
        <option value="sans">Sans lien</option>
      </select>
      <select
        value={filterConformite}
        onChange={(e) => setFilterConformite(e.target.value)}
        className={selectFilterClass}
      >
        <option value="">Conformité</option>
        <option value="ok">Conforme</option>
        <option value="attention">À surveiller</option>
        <option value="critique">Critique</option>
      </select>
    </>
  );

  return (
    <div className="w-full max-w-none space-y-3 text-slate-100">
      <AdminPageHero
        eyebrow="Ressources humaines · Cockpit"
        title="Employés / RH"
        subtitle="Pilotage administratif : contrats, paie, conformité documentaire et comptes — hors exploitation terrain."
        actions={
          <>
            {heroActions}
            {!canManage ? (
              <span className="text-xs text-slate-500">Lecture seule.</span>
            ) : null}
          </>
        }
      />

      {!loading && !loadError && data ? (
        <section className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <AdminKpiCard
            label="Employés actifs"
            value={String(kpis?.employesActifs ?? 0)}
            hint="ACTIF / EN_CONGE"
          />
          <AdminKpiCard
            label="Dossiers incomplets"
            value={String(kpis?.dossiersIncomplets ?? 0)}
            hint="< 85 % complétude"
          />
          <AdminKpiCard
            label="Contrats &lt; 30 j."
            value={String(kpis?.contratsExpirants30j ?? 0)}
            hint="Échéance proche"
          />
          <AdminKpiCard
            label="Journaliers"
            value={String(kpis?.journaliers ?? 0)}
            hint="Profils journaliers"
          />
          <AdminKpiCard
            label="Masse salariale"
            value={formatMoney(kpis?.masseSalarialeMensuelle ?? 0)}
            hint="Estimation actifs"
          />
        </section>
      ) : null}

      {!loading && !loadError && data && alertes ? (
        <section className="rounded-xl border border-amber-500/25 bg-amber-950/15 px-3 py-2 md:px-4 md:py-3">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-200/95">
            <Bell className="h-4 w-4" />
            Alertes RH
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            <div className="flex items-center gap-2 rounded-lg border border-slate-700/80 bg-slate-950/50 px-2 py-1.5">
              <CalendarClock className="h-4 w-4 shrink-0 text-amber-400" />
              <div>
                <div className="text-lg font-bold tabular-nums text-white">
                  {alertes.contratsExpirant30j}
                </div>
                <div className="text-[10px] leading-tight text-slate-400">
                  Contrats &lt; 30 j.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-slate-700/80 bg-slate-950/50 px-2 py-1.5">
              <FileWarning className="h-4 w-4 shrink-0 text-orange-400" />
              <div>
                <div className="text-lg font-bold tabular-nums text-white">
                  {alertes.dossiersIncomplets}
                </div>
                <div className="text-[10px] leading-tight text-slate-400">
                  Dossiers incomplets
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-slate-700/80 bg-slate-950/50 px-2 py-1.5">
              <FileDown className="h-4 w-4 shrink-0 text-rose-400" />
              <div>
                <div className="text-lg font-bold tabular-nums text-white">
                  {alertes.sansDocuments}
                </div>
                <div className="text-[10px] leading-tight text-slate-400">
                  Sans documents requis
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-slate-700/80 bg-slate-950/50 px-2 py-1.5">
              <Landmark className="h-4 w-4 shrink-0 text-sky-400" />
              <div>
                <div className="text-lg font-bold tabular-nums text-white">
                  {alertes.sansCompteBancaire}
                </div>
                <div className="text-[10px] leading-tight text-slate-400">
                  Sans compte (virement)
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-slate-700/80 bg-slate-950/50 px-2 py-1.5">
              <UserX className="h-4 w-4 shrink-0 text-violet-400" />
              <div>
                <div className="text-lg font-bold tabular-nums text-white">
                  {alertes.sansUtilisateur}
                </div>
                <div className="text-[10px] leading-tight text-slate-400">
                  Sans compte utilisateur
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {!loading && !loadError && data ? (
        <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-3 shadow-md backdrop-blur-sm md:p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                type="search"
                placeholder="Recherche globale : nom, matricule, téléphone, poste, type contrat…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full rounded-xl border border-slate-700 bg-slate-950/70 py-2 pl-10 pr-3 text-sm text-white outline-none ring-emerald-500/30 placeholder:text-slate-500 focus:border-emerald-500/50 focus:ring-2"
              />
            </div>
            <span className="flex items-center gap-1 rounded-xl border border-slate-700 bg-slate-950/60 px-2 py-2 text-[11px] text-slate-500 lg:hidden">
              <Filter className="h-3.5 w-3.5 shrink-0" />
              Filtres →
            </span>
          </div>

          {/* Filtres : desktop wrap ; mobile défilement horizontal */}
          <div className="mt-3 border-t border-slate-800 pt-3">
            <div className="-mx-1 flex gap-2 overflow-x-auto pb-1 lg:mx-0 lg:flex-wrap lg:overflow-visible">
              {filterControls}
              <button
                type="button"
                onClick={resetFilters}
                className="shrink-0 rounded-lg border border-slate-600 px-2 py-1.5 text-[11px] text-slate-400 hover:bg-slate-800"
              >
                Réinit.
              </button>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-800 pt-3">
            <button
              type="button"
              onClick={exportExcel}
              className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/15"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              Excel
            </button>
            <button
              type="button"
              onClick={exportPDF}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800/80 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
            >
              <FileDown className="h-3.5 w-3.5" />
              PDF
            </button>
            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-800"
            >
              <RefreshCcw className="h-3.5 w-3.5" />
              Actualiser
            </button>
          </div>
        </section>
      ) : null}

      {loading ? (
        <p className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900/40 py-10 text-center text-sm text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
          Chargement du cockpit RH…
        </p>
      ) : loadError ? (
        <div className="rounded-xl border border-rose-500/30 bg-rose-950/20 px-4 py-6 text-center text-sm text-rose-100">
          Impossible de charger les données. Vérifiez vos droits ou réessayez.
        </div>
      ) : data ? (
        <AdminDataTableShell className="w-full">
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[1280px] table-fixed border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/90">
                  <th className={`${thClass} sticky left-0 z-10 bg-slate-950/95 shadow-[2px_0_8px_rgba(0,0,0,0.3)]`}>
                    <SortBtn label="Employé" k="nomComplet" />
                  </th>
                  <th className={thClass}>
                    <SortBtn label="Matricule RH" k="matriculeRH" />
                  </th>
                  <th className={thClass}>
                    <SortBtn label="Poste RH" k="posteRH" />
                  </th>
                  <th className={thClass}>
                    <SortBtn label="Type contrat" k="typeContrat" />
                  </th>
                  <th className={thClass}>
                    <SortBtn label="Statut RH" k="statutRH" />
                  </th>
                  <th className={thClass}>
                    <SortBtn label="Début contrat" k="dateEmbauche" />
                  </th>
                  <th className={thClass}>
                    <SortBtn label="Fin contrat" k="dateFinContrat" />
                  </th>
                  <th className={thClass}>
                    <SortBtn label="Date création" k="createdAt" />
                  </th>
                  <th className={thClass}>
                    <SortBtn label="Complétude" k="dossierCompletPercent" />
                  </th>
                  <th className={thClass}>Documents</th>
                  <th className={thClass}>
                    <SortBtn label="Paie" k="salaireMensuel" />
                  </th>
                  <th className={thClass}>Compte utilisateur</th>
                  <th className={thClass}>Terrain lié</th>
                  <th className={`${thClass} text-center`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/80">
                {emptyEmployes ? (
                  <tr>
                    <td colSpan={14} className="w-full p-0 align-top">
                      <div className="w-full border-t border-slate-800 bg-slate-950/40 px-4 py-10">
                        <div className="w-full [&>div]:w-full [&_p]:max-w-none">
                          <AdminEmptyState
                            title="Aucun dossier RH"
                            description="Les dossiers créés apparaîtront dans ce tableau (structure ci-dessus)."
                            action={
                              canManage ? (
                                <Link
                                  to="/admin/rh/employes/nouveau"
                                  className={adminHeroActionClassName(false)}
                                >
                                  <PlusCircle className="h-4 w-4" />
                                  Nouvel employé RH
                                </Link>
                              ) : null
                            }
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : emptyFilters ? (
                  <tr>
                    <td colSpan={14} className="w-full p-0 align-top">
                      <div className="w-full border-t border-slate-800 bg-slate-950/40 px-4 py-10 text-center">
                        <p className="text-sm font-medium text-slate-300">Aucun résultat</p>
                        <p className="mt-1 text-xs text-slate-500">Modifiez la recherche ou les filtres.</p>
                        <button
                          type="button"
                          onClick={resetFilters}
                          className="mt-4 rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm text-white hover:bg-slate-800"
                        >
                          Réinitialiser les filtres
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedEmployes.map((row) => (
                    <tr
                      key={row.id}
                      className="cursor-pointer bg-slate-900/30 transition hover:bg-slate-800/60"
                      onClick={() => navigate(`/admin/rh/employes/${row.id}`)}
                    >
                      <td
                        className={`${tdClass} sticky left-0 z-[1] bg-slate-900/95 shadow-[2px_0_8px_rgba(0,0,0,0.25)]`}
                      >
                        <div className="font-semibold text-emerald-200/95">{row.nomComplet}</div>
                        <RhRowBadges row={row} />
                      </td>
                      <td className={`${tdClass} font-mono text-[10px] text-violet-300/90`}>
                        {row.matriculeRH}
                      </td>
                      <td className={tdClass}>
                        <span className="line-clamp-2 max-w-[120px]">{row.posteRH?.trim() || "—"}</span>
                      </td>
                      <td className={tdClass}>{row.typeContrat ?? "—"}</td>
                      <td className={tdClass}>{row.statutRH}</td>
                      <td className={tdClass}>{formatDateFr(row.dateEmbauche)}</td>
                      <td className={tdClass}>{formatDateFr(row.dateFinContrat)}</td>
                      <td className={tdClass}>
                        <div className="text-slate-200">{formatDateFr(row.createdAt)}</div>
                        <div className="text-[9px] text-slate-500">{createdRelative(row.createdAt)}</div>
                      </td>
                      <td className={tdClass}>
                        <div className="flex items-center gap-1">
                          <div className="h-1.5 w-12 overflow-hidden rounded-full bg-slate-800">
                            <div
                              className={`h-full rounded-full ${
                                row.dossierCompletPercent >= 85
                                  ? "bg-emerald-500"
                                  : row.dossierCompletPercent >= 50
                                    ? "bg-amber-500"
                                    : "bg-rose-500"
                              }`}
                              style={{ width: `${row.dossierCompletPercent}%` }}
                            />
                          </div>
                          <span className="tabular-nums text-[10px] text-slate-400">
                            {row.dossierCompletPercent}%
                          </span>
                        </div>
                      </td>
                      <td className={tdClass}>
                        <span
                          className={`inline-flex rounded-full border px-1.5 py-0 text-[10px] font-medium ${
                            row.documentsManquants.length === 0
                              ? "border-emerald-500/35 bg-emerald-500/10 text-emerald-200"
                              : "border-orange-500/35 bg-orange-500/10 text-orange-100"
                          }`}
                        >
                          {row.documentsStatut}
                        </span>
                      </td>
                      <td className={tdClass}>
                        <div className="tabular-nums text-slate-200">
                          {row.salaireMensuel != null ? formatMoney(row.salaireMensuel) : "—"}
                        </div>
                        <div className="text-[9px] text-slate-500">{row.modePaiement ?? "—"}</div>
                      </td>
                      <td className={`${tdClass} max-w-[100px] truncate text-[10px]`} title={row.compteUtilisateur}>
                        {row.compteUtilisateur}
                      </td>
                      <td className={tdClass}>
                        {row.terrainLie ? (
                          <span className="rounded border border-teal-500/35 bg-teal-950/40 px-1.5 py-0 text-[10px] text-teal-100">
                            Oui
                          </span>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className={`${tdClass} text-center`}>
                        <div className="flex justify-center gap-0.5">
                          <button
                            type="button"
                            title="Voir"
                            className="rounded border border-slate-600 bg-slate-800/80 p-1.5 text-sky-300 hover:border-sky-500/40"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/rh/employes/${row.id}`);
                            }}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                          {!row.agentTerrainId && canManage ? (
                            <Link
                              to={`/admin/rh/employes/${row.id}?lier=1`}
                              className="rounded border border-slate-600 bg-slate-800/80 p-1.5 text-teal-300"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Link2 className="h-3.5 w-3.5" />
                            </Link>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </AdminDataTableShell>
      ) : null}

      {!loading && !loadError && data && !emptyEmployes && !emptyFilters ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900/50 px-3 py-2 text-xs text-slate-400">
          <p>
            <span className="font-medium text-emerald-200/90">
              {(pageClamped - 1) * ITEMS_PER_PAGE + 1}
            </span>
            {" – "}
            <span className="font-medium text-emerald-200/90">
              {Math.min(pageClamped * ITEMS_PER_PAGE, filteredEmployes.length)}
            </span>
            {" / "}
            <span className="font-semibold text-white">{filteredEmployes.length}</span>
            {(data.pagination?.total ?? 0) > LIST_LIMIT ? (
              <span className="text-amber-500/90"> · max {LIST_LIMIT} chargés</span>
            ) : null}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pageClamped <= 1}
              className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-40"
            >
              Précédent
            </button>
            <span className="rounded-lg border border-slate-800 bg-slate-950/60 px-2 py-1.5 text-[11px]">
              {pageClamped} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={pageClamped >= totalPages}
              className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-40"
            >
              Suivant
            </button>
          </div>
        </div>
      ) : null}

      {!loading && !loadError && data ? (
        <section className="w-full rounded-xl border border-slate-800 bg-slate-900/50 p-3 md:p-4">
          <h2 className="text-sm font-semibold text-white">Agents sans dossier RH</h2>
          <p className="text-[11px] text-slate-500">
            Profil agent (identité, fonction, statut op.) — sans zone ni équipe. Max. 20 extraits.
          </p>
          {agentsSans.length === 0 ? (
            <p className="py-4 text-center text-xs text-slate-600">Aucun agent actif sans dossier dans cet extrait.</p>
          ) : (
            <div className="mt-2 w-full overflow-x-auto">
              <table className="w-full min-w-[900px] table-fixed border-collapse text-xs">
                <thead>
                  <tr className="border-b border-slate-800 bg-slate-950/80 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    <th className="px-2 py-2 text-left">Agent</th>
                    <th className="px-2 py-2 text-left">Téléphone</th>
                    <th className="px-2 py-2 text-left">Type agent</th>
                    <th className="px-2 py-2 text-left">Fonction terrain</th>
                    <th className="px-2 py-2 text-left">Date création agent</th>
                    <th className="px-2 py-2 text-left">Statut terrain</th>
                    <th className="px-2 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {agentsSans.map((a) => (
                    <tr key={a.id} className="bg-slate-900/20 hover:bg-slate-800/45">
                      <td className="px-2 py-1.5 font-medium text-emerald-100/95">{a.nomComplet}</td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-slate-300">
                        {a.telephone?.trim() || "—"}
                      </td>
                      <td className="px-2 py-1.5 text-slate-300">{libelleTypeAgentRaw(a.typeAgent)}</td>
                      <td className="max-w-[160px] px-2 py-1.5 text-slate-400">
                        <span className="line-clamp-2">{a.fonctionTerrain?.trim() || "—"}</span>
                      </td>
                      <td className="px-2 py-1.5 text-slate-300">
                        <div>{formatDateFr(a.createdAt)}</div>
                        <div className="text-[9px] text-slate-500">{createdRelative(a.createdAt)}</div>
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-slate-300">
                        {libelleStatutOperationnel(a.statutOperationnel)}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {canManage ? (
                          <Link
                            to={`/admin/rh/employes/nouveau?agentId=${a.id}`}
                            className="inline-flex rounded-md bg-violet-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-violet-500"
                          >
                            Créer dossier RH
                          </Link>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
